import { createContext, useContext, useState, useEffect } from 'react'
import { auth, db } from '../services/firebase'
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import { doc, setDoc, getDoc, serverTimestamp, onSnapshot, updateDoc } from 'firebase/firestore'
import adminService from '../services/adminService'
import TrialReminderModal from '../components/TrialReminderModal'

const AuthContext = createContext()

export { AuthContext }

export function useAuth() {
  const context = useContext(AuthContext)
  
  if (!context) {
    console.error('❌ useAuth: No context found - hook must be used within AuthProvider')
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [forceUpdate, setForceUpdate] = useState(0) // Force re-render
  
  // Trial reminder state
  const [showTrialReminder, setShowTrialReminder] = useState(false)
  const [trialData, setTrialData] = useState(null)

  // Debug: Track currentUser state changes
  useEffect(() => {
    if (currentUser && typeof currentUser === 'string') {
      console.log('⚠️ This should not happen - currentUser should always be the full user object or null')
      console.log('⚠️ Attempting to recover by getting the full user object from auth.currentUser')
      
      // Try to recover by getting the full user object
      if (auth.currentUser && auth.currentUser.uid === currentUser) {
        setCurrentUser(auth.currentUser)
      }
    }
  }, [currentUser])


  async function signup(email, password, displayName) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password)
      
      // Update the user's display name
      if (displayName) {
        await updateProfile(result.user, { displayName })
      }
      
      // Create user profile in Firestore
      // Calculate 14-day trial end date
      const trialEndDate = new Date()
      trialEndDate.setDate(trialEndDate.getDate() + 14)

      const userProfile = {
        uid: result.user.uid,
        email: email,
        displayName: displayName || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        role: 'user',
        isActive: true,
        hasRequestedOrganization: false,
        isOrganizationAdmin: false, // Add this field to match mobile app
        // Pro trial fields for new users
        accessLevel: 'pro',
        subscriptionStatus: 'trial',
        subscriptionType: 'pro_trial',
        planType: 'pro_trial',
        trialStartDate: serverTimestamp(),
        trialEndDate: trialEndDate,
        trialDaysRemaining: 14,
        // Pro trial limits
        maxAdmins: 2,
        maxGroupLeaders: 1,
        maxGroups: 5,
        maxAlertsPerMonth: 100,
        // Notification preferences
        notificationPreferences: {
          pushNotifications: true,
          emailNotifications: true,
          alertEmails: true,
          groupEmails: true,
          organizationEmails: true
        }
      }
      
      await setDoc(doc(db, 'users', result.user.uid), userProfile)
      
      // Update admin service with current user
      adminService.setCurrentUser(result.user)
      
      return result
    } catch (error) {
      console.error('❌ AuthContext: Signup failed:', error)
      throw error
    }
  }

  async function login(email, password) {
    try {
      
      const result = await signInWithEmailAndPassword(auth, email, password)
      
      // Force a check of the current auth state
      if (result.user) {
        if (typeof result.user === 'string') {
          const fullUser = auth.currentUser
          if (fullUser && fullUser.uid === result.user) {
            setCurrentUser(fullUser)
          } else {
            console.log('❌ ERROR: Cannot recover full user object, setting to null')
            setCurrentUser(null)
          }
        } else {
          setCurrentUser(result.user)
        }
        
        // Update admin service with current user
        adminService.setCurrentUser(result.user)
        
        await fetchUserProfile(result.user.uid)
        
        // Note: Push notifications are now handled by Cloud Functions
        
        setLoading(false)
        setForceUpdate(prev => prev + 1)
        
        // Force a manual auth state check to ensure everything is updated
        await checkAuthState()
      } else {
      }
      
      return result
    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error)
      throw error
    }
  }

  async function signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)
      
      // Check if user profile exists, if not create one
      const userDoc = await getDoc(doc(db, 'users', result.user.uid))
      if (!userDoc.exists()) {
        const userProfile = {
          uid: result.user.uid,
          email: result.user.email || '',
          displayName: result.user.displayName || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          role: 'user',
          isActive: true,
          hasRequestedOrganization: false,
          isOrganizationAdmin: false // Add this field to match mobile app
        }
        
        await setDoc(doc(db, 'users', result.user.uid), userProfile)
      }
      
      // Update admin service with current user
      if (typeof result.user === 'string') {
        const fullUser = auth.currentUser
        if (fullUser && fullUser.uid === result.user) {
          adminService.setCurrentUser(fullUser)
        } else {
          console.log('❌ ERROR: Cannot recover full user object for Google sign-in')
        }
      } else {
        adminService.setCurrentUser(result.user)
      }
      
      return result
    } catch (error) {
      console.error('Error in Google sign-in:', error)
      throw error
    }
  }

  async function logout() {
    try {
      
      // Clear admin service user
      adminService.setCurrentUser(null)
      
      // Sign out from Firebase
      await signOut(auth)
    } catch (error) {
      console.error('❌ AuthContext: Error during logout:', error)
      throw error
    }
  }

  // Manual function to check and update auth state
  async function checkAuthState() {
    const user = auth.currentUser
    
    if (user) {
      if (typeof user === 'string') {
        const fullUser = auth.currentUser
        if (fullUser && fullUser.uid === user) {
          setCurrentUser(fullUser)
          adminService.setCurrentUser(fullUser)
        } else {
          console.log('❌ ERROR: Cannot recover full user object in checkAuthState')
          setCurrentUser(null)
          adminService.setCurrentUser(null)
        }
      } else {
        setCurrentUser(user)
        adminService.setCurrentUser(user)
      }
      await fetchUserProfile(user.uid)
      setLoading(false)
      setForceUpdate(prev => prev + 1)
    } else {
      setCurrentUser(null)
      setUserProfile(null)
      adminService.setCurrentUser(null)
      setLoading(false)
      setForceUpdate(prev => prev + 1)
    }
  }

  // Function to fix user ID mismatch by signing out and clearing auth state
  async function fixUserMismatch() {
    try {
      
      // Sign out from Firebase
      await signOut(auth)
      
      // Clear all local state
      setCurrentUser(null)
      setUserProfile(null)
      adminService.setCurrentUser(null)
      setLoading(false)
      setForceUpdate(prev => prev + 1)
      
      
    } catch (error) {
      console.error('❌ AuthContext: Error fixing user mismatch:', error)
    }
  }

  async function fetchUserProfile(uid) {
    try {
      if (typeof uid === 'string') {
      } else {
        return
      }
      
      const userDoc = await getDoc(doc(db, 'users', uid))
      if (userDoc.exists()) {
        const profileData = userDoc.data()
        setUserProfile(profileData)
        
        // Check for trial reminder after profile is loaded
        checkTrialReminder(profileData)
      } else {
        console.log('❌ User profile not found - user must sign up first')
        setUserProfile(null)
        // Don't create a user profile - they must sign up properly
      }
    } catch (error) {
      console.error('❌ Error fetching/creating user profile:', error)
    }
  }

  // Check if user should see trial reminder
  function checkTrialReminder(profileData) {
    try {
      // Only check for Pro trials
      if (profileData.accessLevel !== 'pro' || profileData.subscriptionStatus !== 'trial') {
        return
      }

      const trialEndDate = profileData.trialEndDate
      if (!trialEndDate) {
        return
      }

      // Convert Firestore timestamp to Date
      const endDate = trialEndDate.toDate ? trialEndDate.toDate() : new Date(trialEndDate)
      const today = new Date()
      const timeDiff = endDate.getTime() - today.getTime()
      const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24))


      // Check if we should show reminder (20, 10, 5, 3, 2, 1 days)
      const reminderDays = [20, 10, 5, 3, 2, 1]
      if (reminderDays.includes(daysRemaining)) {
        console.log('🔔 Trial reminder: Showing reminder for', daysRemaining, 'days remaining')
        
        // Check if we've already shown this reminder today
        const lastReminderKey = `trial_reminder_${daysRemaining}_${today.toDateString()}`
        const lastReminder = localStorage.getItem(lastReminderKey)
        
        if (!lastReminder) {
          setTrialData({
            daysRemaining,
            trialEndDate: endDate,
            planName: 'Pro'
          })
          setShowTrialReminder(true)
          
          // Mark this reminder as shown today
          localStorage.setItem(lastReminderKey, 'true')
        } else {
        }
      }
    } catch (error) {
      console.error('❌ Error checking trial reminder:', error)
    }
  }

  // Handle trial reminder close
  function handleTrialReminderClose() {
    setShowTrialReminder(false)
    setTrialData(null)
  }

  // Handle upgrade button click
  function handleTrialUpgrade() {
    // Navigate to subscription page
    window.location.href = '/subscription'
  }

  useEffect(() => {
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      
      if (typeof user === 'string') {
        const fullUser = auth.currentUser
        if (fullUser && fullUser.uid === user) {
          setCurrentUser(fullUser)
        } else {
          console.log('❌ ERROR: Cannot recover full user object, setting to null')
          setCurrentUser(null)
        }
      } else {
        setCurrentUser(user)
      }
      
      
      // Update admin service with current user
      if (user) {
        adminService.setCurrentUser(user)
      } else {
        adminService.setCurrentUser(null)
      }
      
      if (user) {
        await fetchUserProfile(user.uid)
        
        // Note: Push notifications are now handled by Cloud Functions
      } else {
        setUserProfile(null)
      }
      setLoading(false)
    })
    
    // Check initial auth state
    const initialUser = auth.currentUser
    
    // If there's already a user, update the state immediately
    if (initialUser) {
      if (typeof initialUser === 'string') {
        const fullUser = auth.currentUser
        if (fullUser && fullUser.uid === initialUser) {
          setCurrentUser(fullUser)
        } else {
          console.log('❌ ERROR: Cannot recover full user object, setting to null')
          setCurrentUser(null)
        }
      } else {
        setCurrentUser(initialUser)
      }
      adminService.setCurrentUser(initialUser)
      fetchUserProfile(initialUser.uid)
      setLoading(false)
    }
    
    return unsubscribe
  }, []) // Empty dependency array

  // Simple real-time listener for user profile changes (for mobile app sync)
  useEffect(() => {
    if (!currentUser) return

    
    const userRef = doc(db, 'users', currentUser.uid)
    const unsubscribeProfile = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const userData = doc.data()
        
        // Update user profile state
        setUserProfile(userData)
        
        // Force update to trigger re-renders in components
        setForceUpdate(prev => prev + 1)
        
      }
    }, (error) => {
      console.error('❌ AuthProvider: Error listening to user profile changes:', error)
    })

    return unsubscribeProfile
  }, [currentUser])

  // Expose global functions for testing and mobile app sync
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Test function for trial reminders (always available)
      window.testTrialReminder = (daysRemaining = 5) => {
        console.log('🧪 Testing trial reminder for', daysRemaining, 'days remaining')
        
        // Create mock trial data
        const mockTrialData = {
          daysRemaining: daysRemaining,
          trialEndDate: new Date(Date.now() + (daysRemaining * 24 * 60 * 60 * 1000)),
          planName: 'Pro'
        }
        
        setTrialData(mockTrialData)
        setShowTrialReminder(true)
        
      }

      // Test function to simulate different urgency levels
      window.testTrialUrgency = () => {
        console.log('🧪 Testing all trial reminder urgency levels...')
        console.log('Available test functions:')
        console.log('- window.testTrialReminder(20) // Low urgency (blue)')
        console.log('- window.testTrialReminder(10) // Medium urgency (yellow)')
        console.log('- window.testTrialReminder(5)  // High urgency (orange)')
        console.log('- window.testTrialReminder(3)  // High urgency (orange)')
        console.log('- window.testTrialReminder(2)  // Critical urgency (red)')
        console.log('- window.testTrialReminder(1)  // Critical urgency (red)')
      }
    }
  }, []) // No dependencies - always available

  // Expose forceUpdateUserProfile globally for mobile app sync
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.forceUpdateUserProfile = async () => {
        if (currentUser) {
          await fetchUserProfile(currentUser.uid)
          setForceUpdate(prev => prev + 1)
        }
      }
    }
  }, [currentUser])

  const value = {
    currentUser,
    userProfile,
    loading,
    forceUpdate,
    signup,
    login,
    signInWithGoogle,
    logout,
    checkAuthState,
    fixUserMismatch
  }

  // Log when any of the key values change
  useEffect(() => {
  }, [currentUser, loading])

  // Log every time the context value is created

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Trial Reminder Modal */}
      {showTrialReminder && trialData && (
        <TrialReminderModal
          isOpen={showTrialReminder}
          onClose={handleTrialReminderClose}
          daysRemaining={trialData.daysRemaining}
          trialEndDate={trialData.trialEndDate}
          planName={trialData.planName}
          onUpgrade={handleTrialUpgrade}
        />
      )}
    </AuthContext.Provider>
  )
}
